import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { MessagesSquare, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { useStore } from '@/shared/store/store-context';

import { AppearanceTab } from '../components/appearance-tab';
import { BansTab } from '../components/bans-tab';
import { BehaviourTab } from '../components/behaviour-tab';
import { ConsentTab } from '../components/consent-tab';
import { DepartmentsTab } from '../components/departments-tab';
import { FormsTab } from '../components/forms-tab';
import { HoursTab } from '../components/hours-tab';
import { ShortcutsTab } from '../components/shortcuts-tab';
import { TriggersTab } from '../components/triggers-tab';
import { useChatSettings } from '../hooks/use-chat-settings';
import { useChatSettingsAccess } from '../hooks/use-chat-settings-access';
import {
  CHAT_SETTINGS_TABS,
  FORM_TABS,
  type ChatSettingsTab,
} from '../schemas/chat-settings-search';

const ROUTE = '/_authed/_active/s/$storeId/settings/chat' as const;

function isTab(value: string): value is ChatSettingsTab {
  return (CHAT_SETTINGS_TABS as readonly string[]).includes(value);
}

function ChatSettings() {
  const { t } = useTranslation('chatSettings');
  const store = useStore();
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: '/s/$storeId/settings/chat' });
  // Prefetched by the route loader.
  const settings = useChatSettings(store.id);
  const chat = settings.data;
  const { manage } = useChatSettingsAccess();
  const tabs = CHAT_SETTINGS_TABS.filter((tab) => tab !== 'bans' || manage);
  const requested = search.tab ?? 'appearance';
  const tab = tabs.includes(requested) ? requested : 'appearance';
  const readOnly = !manage;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        meta={
          chat ? (
            <Badge variant={chat.enabled ? 'default' : 'secondary'}>
              {chat.enabled ? t('status.on') : t('status.off')}
            </Badge>
          ) : null
        }
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/account/chat-profile">
                <UserRound aria-hidden />
                {t('profileLink')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/s/$storeId/inbox" params={{ storeId: store.id }}>
                <MessagesSquare aria-hidden />
                {t('inboxLink')}
              </Link>
            </Button>
          </div>
        }
      />
      {readOnly && FORM_TABS.includes(tab) ? (
        <Alert className="mb-4">
          <AlertDescription>{t('readOnly')}</AlertDescription>
        </Alert>
      ) : null}
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isTab(value)) void navigate({ search: (prev) => ({ ...prev, tab: value }) });
        }}
        className="gap-6"
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <TabsList aria-label={t('tabs.label')}>
            {tabs.map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {chat ? (
          <>
            {/* Form tabs stay mounted so unsaved edits survive a look at another tab (ADR 77). */}
            <TabsContent value="appearance" forceMount hidden={tab !== 'appearance'}>
              <AppearanceTab storeId={store.id} settings={chat} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="hours" forceMount hidden={tab !== 'hours'}>
              <HoursTab storeId={store.id} settings={chat} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="forms" forceMount hidden={tab !== 'forms'}>
              <FormsTab storeId={store.id} settings={chat} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="consent" forceMount hidden={tab !== 'consent'}>
              <ConsentTab storeId={store.id} settings={chat} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="behaviour" forceMount hidden={tab !== 'behaviour'}>
              <BehaviourTab storeId={store.id} settings={chat} readOnly={readOnly} />
            </TabsContent>
          </>
        ) : FORM_TABS.includes(tab) ? (
          <TabsContent value={tab}>
            {settings.isError ? (
              <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
            ) : (
              <Skeleton className="h-64 max-w-3xl" aria-busy="true" />
            )}
          </TabsContent>
        ) : null}
        <TabsContent value="shortcuts">
          <ShortcutsTab storeId={store.id} />
        </TabsContent>
        <TabsContent value="departments">
          <DepartmentsTab storeId={store.id} />
        </TabsContent>
        <TabsContent value="triggers">
          <TriggersTab storeId={store.id} />
        </TabsContent>
        {manage ? (
          <TabsContent value="bans">
            <BansTab storeId={store.id} />
          </TabsContent>
        ) : null}
      </Tabs>
    </PageContent>
  );
}

/** `/s/$storeId/settings/chat`: the live chat widget and the inbox's lists, one tab each. */
export function ChatSettingsPage() {
  return (
    <FeatureErrorBoundary name="chatSettings.page">
      <ChatSettings />
    </FeatureErrorBoundary>
  );
}
